import { Global, css } from '@emotion/react';

import { Typography } from 'chatie-design-system';

import { Platform } from '../constants';

type Direction =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'all'
  | 'horizontal'
  | 'vertical';

type SingleDirection = Extract<Direction, 'top' | 'bottom' | 'left' | 'right'>;

const _directions: { [key in Direction]: SingleDirection[] } = {
  top: ['top'],
  bottom: ['bottom'],
  left: ['left'],
  right: ['right'],
  all: ['top', 'bottom', 'left', 'right'],
  horizontal: ['left', 'right'],
  vertical: ['top', 'bottom'],
};

export const safeArea = (
  direction: SingleDirection,
  type: 'padding' | 'margin' = 'padding',
  min = 0,
  add = 0
) =>
  direction === 'top'
    ? css`
        ${type === 'padding' &&
        css`
          padding-top: max(
            calc(constant(safe-area-inset-top) + ${add}px),
            ${min}px
          );
          padding-top: max(calc(env(safe-area-inset-top) + ${add}px), ${min}px);
        `}
        ${type === 'margin' &&
        css`
          margin-top: max(
            calc(constant(safe-area-inset-top) + ${add}px),
            ${min}px
          );
          margin-top: max(calc(env(safe-area-inset-top) + ${add}px), ${min}px);
        `}
      `
    : direction === 'bottom'
    ? css`
        ${type === 'padding' &&
        css`
          padding-bottom: max(
            calc(constant(safe-area-inset-bottom) + ${add}px),
            ${min}px
          );
          padding-bottom: max(
            calc(env(safe-area-inset-bottom) + ${add}px),
            ${min}px
          );
        `}
        ${type === 'margin' &&
        css`
          margin-bottom: max(
            calc(constant(safe-area-inset-bottom) + ${add}px),
            ${min}px
          );
          margin-bottom: max(
            calc(env(safe-area-inset-bottom) + ${add}px),
            ${min}px
          );
        `}
      `
    : direction === 'left'
    ? css`
        ${type === 'padding' &&
        css`
          padding-left: max(
            calc(constant(safe-area-inset-left) + ${add}px),
            ${min}px
          );
          padding-left: max(
            calc(env(safe-area-inset-left) + ${add}px),
            ${min}px
          );
        `}
        ${type === 'margin' &&
        css`
          margin-left: max(
            calc(constant(safe-area-inset-left) + ${add}px),
            ${min}px
          );
          margin-left: max(
            calc(env(safe-area-inset-left) + ${add}px),
            ${min}px
          );
        `}
      `
    : direction === 'right'
    ? css`
        ${type === 'padding' &&
        css`
          padding-right: max(
            calc(constant(safe-area-inset-right) + ${add}px),
            ${min}px
          );
          padding-right: max(
            calc(env(safe-area-inset-right) + ${add}px),
            ${min}px
          );
        `}
        ${type === 'margin' &&
        css`
          margin-right: max(
            calc(constant(safe-area-inset-right) + ${add}px),
            ${min}px
          );
          margin-right: max(
            calc(env(safe-area-inset-right) + ${add}px),
            ${min}px
          );
        `}
      `
    : undefined;

export const sticky = (top: number) => css`
  position: sticky;
  top: calc(constant(safe-area-inset-top) + ${top}px);
  top: calc(env(safe-area-inset-top) + ${top}px);
`;

export const multilineEllipsis = (line: number) => css`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${line};
  text-overflow: ellipsis;
  overflow: hidden;
`;

export const flashAnimation = css`
  @keyframes flash {
    0% {
      opacity: 1;
    }

    50% {
      opacity: 0;
    }

    100% {
      opacity: 1;
    }
  }
`;

export const linearGradient = (
  direction: SingleDirection,
  from = 'rgba(87, 87, 87, 0)',
  to = 'rgba(0, 0, 0, 0.5)'
) => css`
  background-image: linear-gradient(to ${direction}, ${from}, ${to});
`;

export const marginXTop = (margin: number) => css`
  margin-top: ${margin}px;
`;

export const marginXBottom = (margin: number) => css`
  margin-bottom: ${margin}px;
`;

export const marginXLeft = (margin: number) => css`
  margin-left: ${margin}px;
`;

export const marginXRight = (margin: number) => css`
  margin-right: ${margin}px;
`;

const _marginXMap = {
  top: marginXTop,
  bottom: marginXBottom,
  left: marginXLeft,
  right: marginXRight,
};

export const marginX = (margin: number, direction: Direction) => {
  return _directions[direction].map((d) => _marginXMap[d](margin));
};

export const fontSize = (size: number) => css`
  font-size: ${size}px;
`;

export const shadow = () => css`
  box-shadow: 0px 1px 5px 2px rgba(128, 128, 128, 0.2);
`;

export const textColor = (color: string) =>
  css`
    color: ${color};
  `;

export const MAX_WIDTH = 500;
export const HEADER_HEIGHT = 44;
export const MODAL_HEADER_HEIGHT = 48;

export const GlobalStyle = ({ platform }: { platform: Platform }) => (
  <Global
    styles={css`
      ${flashAnimation}

      ${platform === Platform.ios &&
      `
          .imp-payment-progress {
    top: 50px !important;
  }
`}
      h1 {
        ${Typography.h1}
      }

      h2 {
        ${Typography.h2}
      }

      h3 {
        ${Typography.h3}
      }

      h4 {
        ${Typography.h4}
      }

      h5 {
        ${Typography.h5}
      }

      h6 {
        ${Typography.h6}
      }
      .cv-fullscreen-modal__content {
        .reactEasyCrop_CropArea::before {
          content: '';
          position: absolute;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 34% !important; /* CropArea의 하단 34%를 남기고 상위 66%를 가림 */
          background: rgba(
            0,
            0,
            0,
            0.5
          ) !important; /* 검은색으로 반투명 딤 처리 */
          border: none !important;
        }

        .reactEasyCrop_CropArea::after {
          border: none !important;
        }
      }

      ${platform === Platform.web &&
      css`
        #overlay {
          max-width: ${MAX_WIDTH}px;
          margin: 0 auto;
        }

        .cv-writer {
          max-width: ${MAX_WIDTH}px;
        }
      `}
    `}
  />
);
